import {
  Activity,
  BarChart3,
  Bell,
  Bot,
  Boxes,
  Check,
  ChevronDown,
  CircleDot,
  KeyRound,
  LoaderCircle,
  LogOut,
  QrCode,
  Settings2,
  ShieldCheck,
  X,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { BrandMark } from "./BrandMark";
import { formatRelativeTime } from "../lib/format";
import type { AuthSession, NotificationItem, ViewId } from "../types";
import type { WorkspaceMembership } from "../types";

type AppShellProps = {
  activeView: ViewId;
  children: ReactNode;
  mobileMenuOpen: boolean;
  incidentCount: number;
  notifications: NotificationItem[];
  qodeCount: number;
  resolvableCount: number;
  session: AuthSession;
  workspaces: WorkspaceMembership[];
  workspaceSwitching: boolean;
  onChangePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<void>;
  onLogout: () => Promise<void>;
  onMobileMenuChange: (open: boolean) => void;
  onNotificationRead: (notificationId: string) => Promise<void>;
  onViewChange: (view: ViewId) => void;
  onWorkspaceSwitch: (workspaceId: string) => Promise<void>;
};

const navItems: Array<{
  id: ViewId;
  label: string;
  icon: typeof Bot;
  count?: number | "qodes" | "incidents";
}> = [
  { id: "agent", label: "Agent", icon: Bot },
  { id: "qodes", label: "Qodes", icon: QrCode, count: "qodes" },
  { id: "deployments", label: "Deployments", icon: Boxes },
  { id: "incidents", label: "Incidents", icon: Activity, count: "incidents" },
  { id: "insights", label: "Insights", icon: BarChart3 },
  { id: "beta", label: "Beta Ops", icon: Settings2 },
  { id: "governance", label: "Governance", icon: ShieldCheck },
];

const roleLabels: Record<AuthSession["role"], string> = {
  owner: "所有者",
  admin: "管理员",
  operator: "运营者",
  approver: "审批者",
  analyst: "分析者",
  viewer: "查看者",
};

export function AppShell({
  activeView,
  children,
  mobileMenuOpen,
  incidentCount,
  notifications,
  qodeCount,
  resolvableCount,
  session,
  workspaces,
  workspaceSwitching,
  onChangePassword,
  onLogout,
  onMobileMenuChange,
  onNotificationRead,
  onViewChange,
  onWorkspaceSwitch,
}: AppShellProps) {
  const [accountOpen, setAccountOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationWorking, setNotificationWorking] = useState<string | null>(
    null,
  );
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [accountAction, setAccountAction] = useState<
    "password" | "logout" | null
  >(null);
  const [accountError, setAccountError] = useState("");
  const setView = (view: ViewId) => {
    onViewChange(view);
    onMobileMenuChange(false);
  };
  const initials =
    session.user.displayName.trim().slice(0, 1).toUpperCase() || "Q";
  const unreadCount = notifications.filter((item) => !item.readAt).length;

  const changeAccountPassword = async () => {
    setAccountAction("password");
    setAccountError("");
    try {
      await onChangePassword(currentPassword, newPassword);
    } catch (error) {
      setAccountError(
        error instanceof Error ? error.message : "密码修改未能完成。",
      );
      setAccountAction(null);
    }
  };

  const signOut = async () => {
    setAccountAction("logout");
    setAccountError("");
    try {
      await onLogout();
    } catch (error) {
      setAccountError(error instanceof Error ? error.message : "退出未能完成。");
      setAccountAction(null);
    }
  };

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileMenuOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-brand">
          <BrandMark />
          <span>Qode</span>
          <button
            className="mobile-close"
            type="button"
            aria-label="关闭导航"
            onClick={() => onMobileMenuChange(false)}
          >
            <X size={18} />
          </button>
        </div>

        <nav className="primary-nav" aria-label="主导航">
          {navItems.map((item) => {
            const Icon = item.icon;
            const selected = item.id === activeView;
            const count =
              item.count === "qodes"
                ? qodeCount
                : item.count === "incidents"
                  ? incidentCount
                  : item.count;
            return (
              <button
                className={selected ? "nav-item nav-item-active" : "nav-item"}
                type="button"
                key={item.id}
                onClick={() => setView(item.id)}
              >
                <Icon size={18} strokeWidth={1.8} />
                <span>{item.label}</span>
                {count ? <small>{count}</small> : null}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-spacer" />

        <div className="system-state">
          <CircleDot size={16} />
          <div>
            <strong>解析服务正常</strong>
            <span>{resolvableCount} 个入口在线</span>
          </div>
        </div>

        <button
          className="workspace-switcher"
          type="button"
          aria-expanded={workspaceOpen}
          onClick={() => setWorkspaceOpen((current) => !current)}
        >
          <span className="workspace-symbol">
            <Boxes size={16} strokeWidth={1.8} />
          </span>
          <span className="workspace-context">
            <small>Workspace</small>
            <strong>{session.workspace.name}</strong>
          </span>
          <span className="workspace-role">
            {roleLabels[session.role]}权限
          </span>
          {workspaceSwitching ? (
            <LoaderCircle className="spin" size={16} />
          ) : (
            <ChevronDown size={16} />
          )}
        </button>
        {workspaceOpen ? (
          <div className="workspace-menu">
            {workspaces.map((workspace) => (
              <button
                type="button"
                key={workspace.id}
                disabled={workspaceSwitching}
                onClick={() => {
                  setWorkspaceOpen(false);
                  void onWorkspaceSwitch(workspace.id);
                }}
              >
                <span className="workspace-symbol">
                  <Boxes size={14} strokeWidth={1.8} />
                </span>
                <span>
                  <strong>{workspace.name}</strong>
                  <small>{roleLabels[workspace.role]}权限</small>
                </span>
                {workspace.id === session.workspace.id ? (
                  <Check size={15} />
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
      </aside>

      {mobileMenuOpen ? (
        <button
          className="sidebar-scrim"
          type="button"
          aria-label="关闭导航遮罩"
          onClick={() => onMobileMenuChange(false)}
        />
      ) : null}

      <section className="workspace">
        <header className="topbar">
          <button
            className="mobile-menu"
            type="button"
            aria-label="打开导航"
            onClick={() => onMobileMenuChange(true)}
          >
            <BrandMark />
          </button>
          <div className="topbar-actions">
            <button
              className="icon-button"
              type="button"
              aria-label="通知"
              aria-expanded={notificationsOpen}
              onClick={() => {
                setNotificationsOpen((current) => !current);
                setAccountOpen(false);
              }}
            >
              <Bell size={18} />
              {unreadCount ? <i>{unreadCount > 9 ? "9+" : unreadCount}</i> : null}
            </button>
            {notificationsOpen ? (
              <div className="notification-popover">
                <div className="notification-heading">
                  <strong>通知</strong>
                  <span>{unreadCount} 条未读</span>
                </div>
                <div className="notification-list">
                  {!notifications.length ? (
                    <div className="notification-empty">暂无运行通知</div>
                  ) : null}
                  {notifications.slice(0, 12).map((notification) => (
                    <button
                      className={
                        notification.readAt
                          ? "notification-item notification-item-read"
                          : `notification-item notification-${notification.severity}`
                      }
                      type="button"
                      key={notification.id}
                      disabled={notificationWorking !== null}
                      onClick={async () => {
                        setNotificationWorking(notification.id);
                        try {
                          await onNotificationRead(notification.id);
                          setNotificationsOpen(false);
                        } finally {
                          setNotificationWorking(null);
                        }
                      }}
                    >
                      <span className="notification-dot" />
                      <span>
                        <strong>{notification.title}</strong>
                        <small>{notification.body}</small>
                        <time>{formatRelativeTime(notification.createdAt)}</time>
                      </span>
                      {notificationWorking === notification.id ? (
                        <LoaderCircle className="spin" size={14} />
                      ) : null}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            <button
              className="user-avatar"
              type="button"
              aria-label="打开账户设置"
              onClick={() => {
                setAccountOpen((current) => !current);
                setNotificationsOpen(false);
              }}
            >
              {initials}
            </button>
            {accountOpen ? (
              <div className="account-popover">
                <div className="account-identity">
                  <strong>{session.user.displayName}</strong>
                  <span>@{session.user.username}</span>
                  <small>
                    {session.workspace.name} · {roleLabels[session.role]}权限
                  </small>
                </div>
                <div className="account-password">
                  <label>
                    当前密码
                    <input
                      type="password"
                      autoComplete="current-password"
                      value={currentPassword}
                      onChange={(event) =>
                        setCurrentPassword(event.target.value)
                      }
                    />
                  </label>
                  <label>
                    新密码
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                    />
                  </label>
                  <button
                    className="secondary-button"
                    type="button"
                    disabled={
                      accountAction !== null ||
                      !currentPassword ||
                      newPassword.length < 12
                    }
                    onClick={changeAccountPassword}
                  >
                    {accountAction === "password" ? (
                      <LoaderCircle className="spin" size={14} />
                    ) : (
                      <KeyRound size={14} />
                    )}
                    修改密码
                  </button>
                </div>
                {accountError ? (
                  <div className="account-error">{accountError}</div>
                ) : null}
                <button
                  className="account-logout"
                  type="button"
                  disabled={accountAction !== null}
                  onClick={signOut}
                >
                  {accountAction === "logout" ? (
                    <LoaderCircle className="spin" size={15} />
                  ) : (
                    <LogOut size={15} />
                  )}
                  退出登录
                </button>
              </div>
            ) : null}
          </div>
        </header>
        <main className="main-content">{children}</main>
      </section>
    </div>
  );
}
